import { FloatingWindowController } from "../workspace/floating-window-controller.js";
import {
  UpcomingEarningsDataSource, validateDateRange
} from "../data/upcoming-earnings-data-source.js";
import { RecentEarningsDataSource } from "../data/recent-earnings-data-source.js";
import { calendarDateRange } from "../data/calendar-date-range.js";
import { displayCompanyName, earningsTimePresentation } from "./calendar-display.js";
import "../styles/upcoming-earnings.css";
import { VisibleFeedRefreshController } from "../data/visible-feed-refresh-controller.js";

const VERSION = 2;
const SORT_LABELS = { date: "Date", symbol: "Symbol / company", time: "Time", eps: "Est. EPS" };
let windowSequence = 0;

export class UpcomingEarningsWindow {
  constructor({
    surface, launcher, endpoint = "/api/v1/upcoming-earnings", recentEndpoint = "/api/v1/recent-earnings",
    onOpenChart = async () => {}, announce = () => {}, onStateChange = () => {},
    onFocusWindow = () => {}, nextStackOrder = null,
    storageKey = null, restoreOnConstruct = true, refreshOptions = {}
  }) {
    if (!(surface instanceof HTMLElement)) throw new TypeError("Upcoming earnings requires a workspace surface.");
    if (!(launcher instanceof HTMLButtonElement)) throw new TypeError("Upcoming earnings requires a launcher button.");
    this.surface = surface;
    this.launcher = launcher;
    this.storageKey = storageKey;
    this.onOpenChart = onOpenChart;
    this.announce = announce;
    this.onStateChange = onStateChange;
    this.onFocusWindow = onFocusWindow;
    this.nextStackOrder = nextStackOrder;
    this.dataSource = new UpcomingEarningsDataSource({ endpoint });
    this.recentDataSource = new RecentEarningsDataSource({ endpoint: recentEndpoint });
    this.tabIdPrefix = `chart-earnings-${++windowSequence}`;
    this.abortController = new AbortController();
    this.request = null;
    this.requestSequence = 0;
    this.disposed = false;
    this.opened = false;
    this.loading = false;
    this.backgroundLoading = false;
    this.loadedRange = null;
    this.items = [];
    this.metadata = null;
    this.pendingCharts = new Set();
    this.savedState = readState(storageKey);
    this.assignPreferences(this.savedState);
    this.stackOrder = validStack(this.savedState.stackOrder) ? this.savedState.stackOrder : 1;
    this.element = this.createElement();
    this.element.style.zIndex = String(this.stackOrder);
    this.floating = new FloatingWindowController({
      surface, windowElement: this.element,
      dragHandle: this.element.querySelector("[data-upcoming-earnings-drag]"),
      resizeHandles: this.element.querySelectorAll("[data-window-resize]"),
      minimumWidth: 320, minimumHeight: 480,
      onInteractionEnd: () => this.saveState()
    });
    this.feedRefresh = new VisibleFeedRefreshController({
      ...refreshOptions,
      refresh: ({ now }) => this.refreshStoredFeed(now)
    });
    this.bind();
    this.syncControls();
    if (restoreOnConstruct && this.savedState.open) this.open({ restoreFocus: false });
  }

  createElement() {
    const element = document.createElement("section");
    element.className = "chart-floating-window upcoming-earnings-window";
    element.dataset.upcomingEarningsWindow = "";
    element.dataset.windowSnapId = "upcoming-earnings";
    element.setAttribute("role", "dialog");
    element.setAttribute("aria-label", "Earnings");
    element.hidden = true;
    element.innerHTML = `
      <header class="chart-window-titlebar upcoming-earnings-titlebar" data-upcoming-earnings-drag>
        <strong>Earnings</strong>
        <button type="button" class="upcoming-earnings-close" data-upcoming-earnings-close aria-label="Close earnings" title="Close">&times;</button>
      </header>
      <div class="earnings-tabs" role="tablist" aria-label="Earnings period">
        ${["upcoming", "recent"].map((tab) => `<button type="button" role="tab" id="${this.tabIdPrefix}-${tab}" aria-controls="${this.tabIdPrefix}-panel" data-earnings-tab="${tab}">${tab === "upcoming" ? "Upcoming" : "Recent"}</button>`).join("")}
      </div>
      <div class="earnings-tab-panel" role="tabpanel" id="${this.tabIdPrefix}-panel" data-earnings-panel>
      <div class="upcoming-earnings-summary"><span data-upcoming-count role="status" aria-live="polite"></span><span>Dates follow the New York market calendar</span></div>
      <div class="upcoming-earnings-state" data-upcoming-state role="status"><strong data-upcoming-state-title></strong><p data-upcoming-state-message></p><button type="button" data-upcoming-retry hidden>Retry</button></div>
      <div class="upcoming-earnings-table-scroll" data-upcoming-table-scroll hidden tabindex="0" aria-label="Upcoming earnings results">
        <table class="upcoming-earnings-table" data-upcoming-earnings-table>
          <caption class="chart-sr-only" data-earnings-caption>Upcoming earnings dates. Open a symbol to view its chart.</caption>
          <thead><tr>${Object.entries(SORT_LABELS).map(([key, label]) => `<th scope="col" data-upcoming-sort-header="${key}" aria-sort="none"><button type="button" data-upcoming-sort="${key}" aria-label="Sort by ${label}">${label}<span aria-hidden="true"></span></button></th>`).join("")}</tr></thead>
          <tbody data-upcoming-earnings-rows></tbody>
        </table>
      </div>
      <p class="upcoming-earnings-action-error" data-upcoming-action-error role="alert" hidden></p>
      </div>
      ${["n", "e", "s", "w", "ne", "nw", "se", "sw"].map((edge) => `<span class="chart-resize-handle chart-resize-${edge}" data-window-resize="${edge}" aria-hidden="true"></span>`).join("")}
    `;
    this.surface.append(element);
    return element;
  }

  bind() {
    const options = { signal: this.abortController.signal };
    this.launcher.addEventListener("click", () => this.open(), options);
    this.element.querySelector("[data-upcoming-earnings-close]").addEventListener("click", () => this.close(), options);
    this.element.addEventListener("pointerdown", () => this.focus(), { ...options, capture: true });
    this.element.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault(); this.close();
    }, options);
    for (const tab of this.element.querySelectorAll("[data-earnings-tab]")) {
      tab.addEventListener("click", () => this.selectTab(tab.dataset.earningsTab), options);
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === "Home" ? "upcoming" : event.key === "End" ? "recent"
          : tab.dataset.earningsTab === "upcoming" ? "recent" : "upcoming";
        this.selectTab(next);
      }, options);
    }
    this.element.querySelector("[data-upcoming-retry]").addEventListener("click", () => this.load(), options);
    this.element.addEventListener("click", (event) => {
      const sort = event.target.closest("[data-upcoming-sort]");
      if (sort) {
        const key = sort.dataset.upcomingSort;
        this.sortDirection = this.sortKey === key && this.sortDirection === "asc" ? "desc" : "asc";
        this.sortKey = key; this.renderResults(); this.saveState();
      }
      const chart = event.target.closest("[data-upcoming-chart]");
      if (chart) this.openChart(chart.dataset.upcomingChart, chart);
    }, options);
  }

  open({ restoreFocus = true } = {}) {
    if (this.disposed) return;
    if (!this.opened) {
      this.element.hidden = false; this.opened = true;
      this.launcher.setAttribute("aria-pressed", "true");
      this.floating.applyGeometry(validGeometry(this.savedState.geometry) ? this.savedState.geometry : this.defaultGeometry());
      this.floating.constrainToSurface();
      Object.assign(this, earningsDateRange(this.tab, this.feedRefresh.instant()));
      this.syncControls();
      this.feedRefresh.start();
      this.load();
      this.announce("Earnings opened.");
    }
    this.focus(); this.saveState();
    if (restoreFocus) this.element.querySelector(`[data-earnings-tab="${this.tab}"]`).focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.opened) return;
    this.savedState.geometry = this.floating.snapshot();
    this.opened = false; this.element.hidden = true;
    this.element.classList.remove("is-focused");
    this.launcher.setAttribute("aria-pressed", "false");
    this.feedRefresh.stop();
    this.request?.abort(); this.requestSequence += 1; this.loading = false;
    this.saveState();
    if (restoreFocus) this.launcher.focus({ preventScroll: true });
    this.announce("Earnings closed.");
  }

  focus() {
    if (!this.opened || this.disposed) return;
    this.onFocusWindow(this);
    const next = this.nextStackOrder?.();
    if (validStack(next)) this.stackOrder = next;
    this.element.style.zIndex = String(this.stackOrder);
    this.element.classList.add("is-focused");
    this.saveState();
  }

  refreshStoredFeed(now) {
    if (!this.opened || this.disposed || this.loading || !this.feedRefresh.visible()) return;
    const dates = earningsDateRange(this.tab, now);
    if (dates.from !== this.from || dates.to !== this.to) {
      Object.assign(this, dates); this.syncControls(); this.saveState();
    }
    return this.load({ automatic: true });
  }

  selectTab(tab) {
    if (!["upcoming", "recent"].includes(tab) || this.disposed) return;
    let request = null;
    if (tab !== this.tab) {
      this.tab = tab; this.sortKey = "date"; this.sortDirection = tab === "recent" ? "desc" : "asc";
      Object.assign(this, earningsDateRange(tab, this.feedRefresh.instant()));
      this.syncControls(); this.saveState(); this.scrollToTop();
      request = this.load();
    }
    this.element.querySelector(`[data-earnings-tab="${tab}"]`).focus({ preventScroll: true });
    return request;
  }

  async load({ automatic = false } = {}) {
    this.request?.abort();
    const sequence = ++this.requestSequence;
    const requestedAt = this.feedRefresh.instant();
    const sameRange = this.loadedRange === this.rangeKey();
    const previousContent = JSON.stringify({ items: this.items, metadata: this.metadata });
    this.backgroundLoading = automatic && sameRange && !this.error;
    if (!this.backgroundLoading) { this.items = []; this.metadata = null; this.loadedRange = null; }
    this.loading = true; this.error = null;
    let unchanged = false;
    if (!automatic) this.clearActionError();
    if (this.backgroundLoading) { this.element.setAttribute("aria-busy", "true"); this.updateResultCount(); }
    else this.renderResults();
    try {
      validateDateRange(this.from, this.to);
      const request = new AbortController(); this.request = request;
      const range = this.rangeKey();
      const source = this.tab === "recent" ? this.recentDataSource : this.dataSource;
      const result = await source.load({
        from: this.from, to: this.to, signal: request.signal,
        onProgress: ({ loaded, total }) => {
          if (sequence === this.requestSequence && this.opened && !this.backgroundLoading) this.setCount(`Loading ${loaded.toLocaleString()} of ${total.toLocaleString()} earnings…`);
        }
      });
      if (sequence !== this.requestSequence || this.disposed || !this.opened) return;
      unchanged = automatic && sameRange && previousContent === JSON.stringify(result);
      this.items = result.items; this.metadata = result.metadata;
      this.loadedRange = range;
    } catch (error) {
      if (sequence !== this.requestSequence || this.disposed || error?.name === "AbortError") return;
      this.items = []; this.metadata = null; this.loadedRange = null;
      this.error = error instanceof Error ? error.message : "Earnings could not be loaded. Please try again.";
    } finally {
      if (sequence === this.requestSequence && !this.disposed) {
        this.loading = false; this.backgroundLoading = false; this.request = null;
        if (unchanged) { this.element.setAttribute("aria-busy", "false"); this.updateResultCount(); }
        else {
          const scroll = this.element.querySelector("[data-upcoming-table-scroll]");
          const position = automatic && sameRange ? { top: scroll.scrollTop, left: scroll.scrollLeft } : null;
          this.renderResults();
          if (position) { scroll.scrollTop = position.top; scroll.scrollLeft = position.left; }
        }
        this.feedRefresh.markRefreshed(requestedAt);
      }
    }
  }

  renderResults() {
    if (!this.opened) return;
    const state = this.element.querySelector("[data-upcoming-state]");
    const scroll = this.element.querySelector("[data-upcoming-table-scroll]");
    const tbody = this.element.querySelector("[data-upcoming-earnings-rows]");
    this.element.setAttribute("aria-busy", String(this.loading));
    state.hidden = false; scroll.hidden = true;
    this.element.querySelector("[data-upcoming-retry]").hidden = !this.error;
    if (this.loading && !this.backgroundLoading || this.error) {
      tbody.replaceChildren();
      this.element.querySelector("[data-upcoming-state-title]").textContent = this.loading ? `Loading ${this.tab} earnings` : "Unable to load earnings";
      this.element.querySelector("[data-upcoming-state-message]").textContent = this.loading ? "Checking all results in this date range…" : this.error;
      this.setCount(this.loading ? "Loading earnings…" : "Earnings unavailable");
      return;
    }
    const filtered = sortEarnings(this.items, this);
    this.updateResultCount(filtered);
    if (!filtered.length) {
      tbody.replaceChildren();
      this.element.querySelector("[data-upcoming-state-title]").textContent = this.tab === "recent" ? "No recent reported earnings found" : "No upcoming earnings found";
      this.element.querySelector("[data-upcoming-state-message]").textContent = this.tab === "recent"
        ? "No verified reported earnings with actual EPS are available for the last 30 days. Coverage is partial."
        : "No current earnings dates are available for the next 30 days. Coverage is partial.";
    } else {
      state.hidden = true; scroll.hidden = false;
      const fragment = document.createDocumentFragment();
      for (const item of filtered) fragment.append(this.createRow(item));
      tbody.replaceChildren(fragment);
    }
    for (const header of this.element.querySelectorAll("[data-upcoming-sort-header]")) {
      const selected = header.dataset.upcomingSortHeader === this.sortKey;
      header.setAttribute("aria-sort", selected ? (this.sortDirection === "asc" ? "ascending" : "descending") : "none");
      header.querySelector("button span").textContent = selected ? (this.sortDirection === "asc" ? "↑" : "↓") : "";
    }
  }

  createRow(item) {
    const row = node("tr"); row.dataset.upcomingEarningsId = item.id;
    const date = node("td", "", "upcoming-earnings-date");
    const eventDate = this.tab === "recent" ? item.reportedDate : item.expectedAnnouncementDate;
    const time = node("time", formatDate(eventDate)); time.dateTime = eventDate; date.append(time);
    const company = node("td");
    const chart = node("button", item.symbol, "upcoming-earnings-symbol");
    chart.type = "button"; chart.dataset.upcomingChart = item.id;
    chart.setAttribute("aria-label", `Open chart for ${item.symbol}`);
    chart.title = `Open ${displayCompanyName(item.companyName)} chart`;
    chart.disabled = this.pendingCharts.has(item.id);
    company.append(chart, node("span", displayCompanyName(item.companyName), "upcoming-earnings-company"));
    const reportTime = node("td", "", "upcoming-earnings-time");
    reportTime.append(reportTimeIcon(item));
    const eps = node("td", "", "upcoming-earnings-eps");
    const value = this.tab === "recent" ? item.actualEps : item.estimatedEps;
    const label = this.tab === "recent" ? "Actual EPS" : "Estimated EPS";
    const epsValue = node("span", value == null ? "—" : String(value));
    const basis = item.epsBasisNote || "The source does not specify an EPS basis.";
    epsValue.title = value == null ? "No estimated EPS supplied" : basis;
    epsValue.setAttribute("aria-label", value == null ? "No estimated EPS supplied" : `${label} ${String(value)}. ${basis}`);
    eps.append(epsValue);
    row.append(date, company, reportTime, eps);
    return row;
  }

  async openChart(id, button) {
    const item = this.items.find((entry) => entry.id === id);
    if (!item || this.pendingCharts.has(id)) return;
    this.pendingCharts.add(id); button.disabled = true; this.clearActionError();
    try { await this.onOpenChart(item); }
    catch (error) {
      if (this.disposed) return;
      const message = error instanceof Error && error.message ? error.message : `The ${item.symbol} chart could not be opened. Please try again.`;
      const alert = this.element.querySelector("[data-upcoming-action-error]");
      alert.textContent = message; alert.hidden = false; this.announce(message);
    } finally {
      this.pendingCharts.delete(id); if (!this.disposed) button.disabled = false;
    }
  }

  updateResultCount(filtered = sortEarnings(this.items, this)) {
    this.setCount(`${filtered.length.toLocaleString()} ${filtered.length === 1 ? "result" : "results"} · ${this.tab === "recent" ? "Last" : "Next"} 30 days${this.backgroundLoading ? " · Checking for updates…" : ""}`);
  }
  setCount(message) { this.element.querySelector("[data-upcoming-count]").textContent = message; }
  clearActionError() { this.element.querySelector("[data-upcoming-action-error]").hidden = true; }
  scrollToTop() { this.element.querySelector("[data-upcoming-table-scroll]").scrollTop = 0; }
  rangeKey() { return `${this.tab}/${this.from}/${this.to}`; }
  syncControls() {
    for (const tab of this.element.querySelectorAll("[data-earnings-tab]")) {
      const selected = tab.dataset.earningsTab === this.tab;
      tab.setAttribute("aria-selected", String(selected)); tab.tabIndex = selected ? 0 : -1;
    }
    this.element.querySelector("[data-earnings-panel]").setAttribute("aria-labelledby", `${this.tabIdPrefix}-${this.tab}`);
    const eps = this.element.querySelector('[data-upcoming-sort="eps"]');
    const label = this.tab === "recent" ? "Actual EPS" : "Est. EPS";
    eps.firstChild.textContent = label; eps.setAttribute("aria-label", `Sort by ${label}`);
    this.element.querySelector("[data-earnings-caption]").textContent = this.tab === "recent"
      ? "Recent reported earnings with actual EPS. Open a symbol to view its chart."
      : "Upcoming earnings dates with estimated EPS. Open a symbol to view its chart.";
    this.element.querySelector("[data-upcoming-table-scroll]").setAttribute("aria-label", this.tab === "recent" ? "Recent earnings results" : "Upcoming earnings results");
  }
  assignPreferences(state) {
    this.tab = state.version === VERSION && state.tab === "recent" ? "recent" : "upcoming";
    Object.assign(this, earningsDateRange(this.tab));
    this.sortKey = state.version === VERSION && Object.hasOwn(SORT_LABELS, state.sortKey) ? state.sortKey : "date";
    this.sortDirection = state.version === VERSION && ["asc", "desc"].includes(state.sortDirection)
      ? state.sortDirection : this.tab === "recent" ? "desc" : "asc";
  }
  defaultGeometry() {
    const width = Math.min(960, this.surface.clientWidth - 16);
    const height = Math.min(620, this.surface.clientHeight - 16);
    return { x: Math.max(0, (this.surface.clientWidth - width) / 2), y: Math.max(0, Math.min(40, this.surface.clientHeight - height)), width: Math.max(0, width), height: Math.max(0, height) };
  }
  snapshotState() {
    return { version: VERSION, open: this.opened, geometry: this.opened ? this.floating.snapshot() : this.savedState.geometry,
      stackOrder: this.stackOrder, tab: this.tab, sortKey: this.sortKey, sortDirection: this.sortDirection };
  }
  saveState() {
    this.savedState = { version: VERSION, ...this.snapshotState() };
    if (this.storageKey) { try { localStorage.setItem(this.storageKey, JSON.stringify(this.savedState)); } catch { /* The tool remains usable without storage. */ } }
    this.onStateChange(this.snapshotState());
  }
  setStorageKey(key) {
    const next = String(key || "").trim();
    if (!next) throw new TypeError("Upcoming earnings storage requires a product-qualified key.");
    this.storageKey = next; this.savedState = readState(next);
    this.assignPreferences(this.savedState); this.syncControls();
  }
  restoreState(state) {
    if (!state || typeof state !== "object") state = this.savedState;
    this.assignPreferences(state); this.syncControls();
    this.savedState = { version: VERSION, geometry: validGeometry(state.geometry) ? state.geometry : this.savedState.geometry };
    if (validStack(state.stackOrder)) { this.stackOrder = state.stackOrder; this.element.style.zIndex = String(this.stackOrder); }
    if (state.open === true) {
      if (!this.opened) this.open({ restoreFocus: false });
      else {
        if (validGeometry(this.savedState.geometry)) this.floating.applyGeometry(this.savedState.geometry);
        this.floating.constrainToSurface();
        if (this.loadedRange !== this.rangeKey()) this.load(); else this.renderResults();
      }
    } else if (this.opened) this.close({ restoreFocus: false });
  }
  dispose() {
    this.disposed = true; this.requestSequence += 1; this.request?.abort();
    this.feedRefresh.dispose();
    this.abortController.abort(); this.floating.dispose(); this.element.remove();
    this.launcher.setAttribute("aria-pressed", "false");
  }
}

function node(tag, text = "", className = "") { const element = document.createElement(tag); element.textContent = text; if (className) element.className = className; return element; }
function readState(key) { if (!key) return {}; try { const value = JSON.parse(localStorage.getItem(key) || "{}"); return [1, VERSION].includes(value?.version) ? value : {}; } catch { return {}; } }
function validGeometry(value) { return value && ["x", "y", "width", "height"].every((key) => Number.isFinite(value[key])) && value.width > 0 && value.height > 0; }
function validStack(value) { return Number.isFinite(value) && value > 0; }
function formatDate(value) { const parsed = new Date(`${value}T00:00:00Z`); return Number.isFinite(parsed.getTime()) ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(parsed) : String(value || ""); }
function reportTimeIcon(item) {
  const presentation = earningsTimePresentation(item.reportTime, item.reportTimeZone);
  const icon = node("span", presentation.kind === "unknown" ? "—" : "", "earnings-report-time");
  icon.dataset.earningsTime = presentation.kind;
  icon.setAttribute("role", "img"); icon.setAttribute("aria-label", presentation.label); icon.title = presentation.label;
  if (presentation.kind !== "unknown") {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24"); svg.setAttribute("aria-hidden", "true"); svg.setAttribute("focusable", "false");
    svg.innerHTML = presentation.kind === "sun"
      ? '<circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5"/>'
      : presentation.kind === "moon" ? '<path d="M20 14.2A8.5 8.5 0 0 1 9.8 4 8.5 8.5 0 1 0 20 14.2Z"/>'
        : '<circle cx="12" cy="12" r="8"/><path d="M12 7v5l3 2"/>';
    icon.append(svg);
  }
  return icon;
}

export const earningsDateRange = calendarDateRange;

export function sortEarnings(items, { tab = "upcoming", sortKey = "date", sortDirection = tab === "recent" ? "desc" : "asc" } = {}) {
  const key = Object.hasOwn(SORT_LABELS, sortKey) ? sortKey : "date";
  const direction = sortDirection === "desc" ? -1 : 1;
  const date = (item) => tab === "recent" ? item.reportedDate : item.expectedAnnouncementDate;
  const value = (item) => ({ date: date(item), symbol: item.symbol, time: item.reportTime, eps: tab === "recent" ? item.actualEps : item.estimatedEps })[key];
  const compare = (a, b) => String(a || "").localeCompare(String(b || ""), "en", { numeric: true });
  return [...items].sort((a, b) => {
    const left = value(a); const right = value(b);
    if (key === "eps" && (left == null || right == null)) {
      if (left == null && right != null) return 1;
      if (right == null && left != null) return -1;
    }
    const primary = key === "eps" && left != null && right != null ? left - right : compare(left, right);
    return primary * direction || compare(date(a), date(b)) || compare(a.symbol, b.symbol) || compare(a.id, b.id);
  });
}
